'use strict';

require('./bootstrap');

import axios from 'axios';
import ClipboardJS from 'clipboard';

/**
 * @summary Common regular expressions
 */
export const RegexPatterns = {

  // Email
  // See https://emailregex.com for more info about this regex
  // eslint-disable-next-line no-control-regex
  email: /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,

  // Date
  date: /^([0-2]\d|3[01])\/(0\d|1[0-2])\/(19[2-9]\d|2[01]\d{2})$/,

  // Mobile
  mobile: /^0[45]\d{2} \d{3} \d{3}$/,

  // Landline
  landline: /^\(0[23789]\) \d{2} \d{3} \d{3}$/,
};


/**
 * @summary This callback is executed when the page has completed loading / document state is no longer set as 'loading'.
 * @callback cb
 */
export function pageReady(callback) {

  // Execute the callback immediately if the page is already loaded
  if (document.readyState != 'loading') {
    callback();
  }
  // Execute the callback once page is done loading
  else {
    document.addEventListener('DOMContentLoaded', callback);
  }
}


/**
 * @summary Submit all requests with the data provided using axios.
 *
 * @param {string} type type of request eg. GET, POST
 * @param {string} url url to submit the request to
 * @param {object} data object containing data to submit
 * @param {string} [res=json] response type if not needed in json
 * @param {string} [contentType] content type
 *
 * @returns {object|string} response json object or plaintext
 */
export async function request(type, url, data, res = 'json', contentType = null) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content') || '';

  let values = {
    method: type,
    url: url,
    responseType: res,
    headers: {
      'Accept': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    }
  };

  // Set the data key based on type
  values[(type == 'GET' ? 'params' : 'data')] = data;

  // Check if content type has been provided
  if (typeof contentType === 'string') {
    values['headers']['Content-Type'] = contentType;
  }

  try {
    // Lets try submit the request
    const response = await axios(values);

    // Return the response data
    return res === 'json' ? response.data : response;
  } catch (error) {
    return { success: false, error: error.response.status, data: error.response.data };
  }
}


/**
 * @summary Disable/enable form submit buttons
 * @description Helper function to toggle form submit button
 *
 * @param {HTMLButtonElement} submitButton button element to toggle
 * @param {boolean} [enabled=false] whether to enable or disable the button
 */
export function toggleSubmit(submitButton, enabled = false) {

  // Show/hide loading indicator (spinner)
  submitButton.setAttribute('data-kt-indicator', enabled ? 'off' : 'on');

  // Disable/enable button
  submitButton.disabled = !enabled;
}


/**
 * @summary Wrapper for alert dialogs
 * @description Helper function to display a dialog using SweetAlert.
 *
 * @param {string} msgHtml html to display
 * @param {boolean} [isError=false] whether to use error icons and colors
 * @param {boolean} [isBlocking=false] whether to prevent user from clicking outside to dismiss
 * @param {string} [confirmButtonText=OK] text to display on the confirm button
 */
export function showAlert(msgHtml, isError = false, isBlocking = false, confirmButtonText = 'OK') {

  const hasLongText = msgHtml.length > 220;

  return Swal.fire({
    html: msgHtml,
    icon: isError ? 'error' : 'success',
    allowOutsideClick: !isBlocking,
    allowEscapeKey: !isBlocking,
    confirmButtonText: confirmButtonText,
    customClass: {
      htmlContainer: hasLongText ? 'text-start' : '',
      confirmButton: `btn btn-${isError ? 'danger' : 'success'}`,
    },
    buttonsStyling: false,
  });
}


/**
 * @summary Wrapper for confirm dialogs (blocking)
 * @description Helper function to display a confirmation dialog using SweetAlert. User must click one of the two buttons to proceed.
 *
 * @param {string} text text to display
 * @param {string} [confirmButtonText=Yes] text to display on the confirm button
 * @param {boolean} [confirmButtonDanger=true] whether the operation is dangerous and should be displayed in red
 * @param {string} [cancelButtonText=Cancel] text to display on the cancel button
 */
export function showConfirm(text, confirmButtonText = 'Yes', confirmButtonDanger = true, cancelButtonText = 'Cancel') {

  return Swal.fire({
    text: text,
    icon: 'warning',
    allowOutsideClick: false,
    allowEscapeKey: true,
    showCancelButton: true,
    confirmButtonText: confirmButtonText,
    cancelButtonText: cancelButtonText,
    customClass: {
      confirmButton: `btn btn-${confirmButtonDanger ? 'danger' : 'success'}`,
      cancelButton: 'btn btn-white',
    },
    buttonsStyling: false,
  });
}


/**
 * @summary Checks if an element is completely within the viewport
 * @description Helper function to check if an element is completely visible in the viewport
 *
 * @param {HTMLElement} el - element to check
 * @param {Number} offset - offset in px from the top of the viewport to account for fixed elements (eg. navbar)
 * @returns {boolean} - true if element exists and is completely visible
 */
export const isInViewport = function (el, offsetTop = 0) {
  const rect = el?.getBoundingClientRect();
  return (
    el &&
    rect.top >= offsetTop &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
  );
};


/**
 * @summary Scrolls to start of element
 * @description Helper function to scroll to the start of an element
 * @link https://stackoverflow.com/a/49860927
 *
 * @param {HTMLElement} el - element to scroll to
 * @param {Number} offsetTop - offset in px from the top of the viewport to account for fixed elements (eg. navbar)
 */
export const scrollToElem = function (el, offsetTop = 0) {
  if (!el) return;
  const elTop = el.getBoundingClientRect().top;
  const pageTop = elTop + window.pageYOffset - offsetTop;
  window.scrollTo({
    top: pageTop,
    behavior: 'smooth'
  });
};


/**
 * @summary Format dates
 * @description Format dates on the page
 */
export const formatDates = function () {

  // Date formats
  const dateFormats = {

    // Date only
    'dateFull': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }, // Monday, January 1, 2019
    'dateLong': { year: 'numeric', month: 'long', day: 'numeric' }, // 1 January 2019
    'dateShort': { year: 'numeric', month: 'short', day: 'numeric' }, // 1 Jan 19
    'dateNumeric': { year: 'numeric', month: 'numeric', day: 'numeric' }, // 01/01/19

    // Month and year only
    'monthYearLong': { year: 'numeric', month: 'long' }, // January 2019
    'monthYearShort': { year: 'numeric', month: 'short' }, // Jan 2019
    'monthYearNumeric': { year: 'numeric', month: 'numeric' }, // 01/2019

    // Date and time
    'dateTimeNumeric': { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }, // 01/01/19 10:00 am
    'dateTimeSecsNumeric': { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }, // 01/01/19 10:00:00 am
  };

  // Get all dates
  const dateEls = document.querySelectorAll('[data-date]');
  dateEls.forEach(el => {
    const format = el.dataset.dateformat || 'dateNumeric'; // default
    let date = el.dataset.date;

    // Check if date is in milliseconds or unix format
    if (date.length === 13) {
      date = new Date(date);
    }
    else {
      date = new Date(Number(date) * 1000);
    }

    // If a valid date, output formatted date
    if (!isNaN(date) && date.getTime() > 0) {
      let dateString = date.toLocaleDateString('en-AU', dateFormats[format]);

      // If format includes time, ensure time cannot be split across lines
      if (/time/i.test(format)) {
        dateString = '<span class="text-nowrap">' + dateString.replace(/(,?)\s+/, '$1</span> <span class="text-nowrap">') + '</span>';
      }

      // Set date into element text
      el.innerHTML = dateString;
    }
  });
};


/**
 * ============================================================
 * Main App Functions
 * ============================================================
 * @description App-level scripts to be run once on page load
 */
const app = function () {

  /**
   * @description Inits input masking for form fields using HTML attributes
   * @documentation https://github.com/RobinHerbots/Inputmask
   * @package https://www.npmjs.com/package/inputmask
   *
   * @example <input type="text" data-inputmask="'mask': '99/99/9999'">
   * @example <input type="text" data-inputmask-regex="0[45]\d{2} \d{3} \d{3}">
  */
  const inputMasking = function () {
    Inputmask().mask(document.querySelectorAll('input'));
  };

  /**
   * @summary Active menu link states
   * @description Checks if menu links is current page, and set active state
   */
  const activeMenuLinks = function () {

    // Top-level header menu links
    const headerLinks = document.querySelectorAll('.header-menu .menu-item > .menu-link');
    headerLinks.forEach(link => {

      // If not a valid link, skip setting active class
      if (!link.getAttribute('href') || link.getAttribute('href') === '#' || link.hostname !== location.hostname) return;

      // Cache parent element
      const parent = link.parentElement;

      // If link is current path
      if (link.pathname === location.pathname) {
        link.classList.add('active');

        // Header link, set class to parent element
        if (parent.classList.contains('menu-item')) {
          parent.classList.add('here');
        }
      }

      // If not root link, and link is parent of current path
      if (link.pathname !== '/' && location.pathname.startsWith(link.pathname)) {

        // Header link, set class to parent element
        if (parent.classList.contains('menu-item')) {
          parent.classList.add('here');
        }
      }
    });

    // All header submenu and sidebar links
    const menuLinks = document.querySelectorAll('.menu-link');
    menuLinks.forEach(link => {
      // If not a valid link, skip
      if (!link.getAttribute('href') || link.getAttribute('href') === '#' || link.hostname !== location.hostname) return;

      // If link is current path
      if (link.pathname === location.pathname) {
        link.classList.add('active');
      }
      // If we are on a tickets sub-page (except create), also set the /support/tickets link as active
      else if (location.pathname.startsWith('/support/tickets') && !location.pathname.endsWith('/create') && link.pathname === '/support/tickets') {
        link.classList.add('active');
      }
    });

    // Allow header links to be clickable when it's not in mobile/drawer mode
    const desktopMenu = document.querySelector('.header-menu:not(.drawer)');
    desktopMenu?.querySelectorAll('a:not([target])').forEach(el => {
      el.addEventListener('click', () => { window.location = el.href; });
    });

    // Improve keyboard accessibility for the menu buttons which have a dropdown
    document.querySelectorAll('.menu-item[data-kt-menu-trigger] > a').forEach(el => {
      el.addEventListener('keydown', function (evt) {
        // Only listen to enter event
        if (evt.key !== 'Enter') return;
        this.dispatchEvent(new MouseEvent('click'));
      });
    });
  };

  /**
   * @summary Click event on toggle dark mode button
   */
  const initDarkModeToggle = () => {
    const htmlEl = document.documentElement;
    const themeStylesheet = document.getElementById('theme-stylesheet');
    const darkModeToggle = document.getElementById('dark-mode-toggle');

    // Function to toggle dark mode images
    const toggleDarkImages = function (darkmode = false) {
      document.querySelectorAll('img').forEach(img => {
        if (!img.dataset.dark) return; // image does not have a dark mode variant set
        if (darkmode) {
          img.setAttribute('data-light', img.src);
          img.setAttribute('src', img.dataset.dark);
        }
        else {
          img.setAttribute('src', img.dataset.light);
        }
      });
    };

    // Check if elements are used on page, otherwise do nothing
    if (!themeStylesheet || !darkModeToggle) return;

    // If body has class "darkmode", set dark mode toggle to checked
    if (htmlEl.classList.contains('dark-mode')) {
      darkModeToggle.checked = true;
      toggleDarkImages(true);
    }

    // Handle dark mode button click event
    darkModeToggle.addEventListener('click', (evt) => {
      evt.target.blur();

      // Temporarily stops transitions on entire page
      htmlEl.classList.toggle('no-transitions');

      // Toggle the dark-mode class
      htmlEl.classList.toggle('dark-mode');
      const isDarkMode = htmlEl.classList.contains('dark-mode');

      // Switch stylesheet source from light to dark
      themeStylesheet.href = isDarkMode ? themeStylesheet.href.replace('light', 'dark') : themeStylesheet.href.replace('dark', 'light');

      // Send new state to server
      request('POST', '/account/dark-mode', { dark_mode: isDarkMode });

      // Toggle images
      toggleDarkImages(isDarkMode);

      // Re-enable transitions
      setTimeout(() => {
        htmlEl.classList.toggle('no-transitions');
      }, 200);
    });
  };

  /**
   * @summary Page-level errors & messages popup
   * @description Detect page-level form post-back errors/messages and display them
   */
  const handlePageLevelAlerts = function () {

    // Check if element with class form_errors exist on page
    const successEl = document.getElementById('form_success');
    const errorEl = document.getElementById('form_errors');
    if (!successEl && !errorEl) return;

    const isError = errorEl;
    let el = isError ? errorEl : successEl;

    // Copy and remove errors from the page, and remove empty errors, just to be safe
    const msgHtml = el.innerHTML.replace(/<li>\s*<\/li>/g, '').replace(/(^\s+|\s+$)/g, '');
    el.parentNode.removeChild(el);

    // Do nothing if no messages
    if (msgHtml === '') return;

    // Display the message
    showAlert(msgHtml, isError);
  };

  /**
   * @summary Updates scroll position
   * @description Debounce scroll event and store scroll position on the html element so we can target it via CSS.
   *   When the document is not at the top, the fixed header should have a white background.
   *   For more information, see https://css-tricks.com/styling-based-on-scroll-position
   */
  const updateWindowScrollPosition = function () {

    // The debounce function receives our function as a parameter
    const debounce = (fn) => {
      // This holds the requestAnimationFrame reference, so we can cancel it if we wish
      let frame;

      // The debounce function returns a new function that can receive a variable number of arguments
      return (...params) => {
        // If the frame variable has been defined, clear it now, and queue for next frame
        if (frame) cancelAnimationFrame(frame);

        // Queue our function call for the next frame
        frame = requestAnimationFrame(() => {
          // Call our function and pass any params we received
          fn(...params);
        });
      };
    };

    // Reads out the scroll position and stores it in the data attribute
    // so we can use it in our stylesheets
    const storeScroll = () => {
      document.documentElement.dataset.scroll = window.scrollY;
    };

    // Listen for new scroll events, here we debounce our `storeScroll` function
    document.addEventListener('scroll', debounce(storeScroll), { passive: true });

    // Update scroll position for first time
    storeScroll();
  };

  /**
   * @summary Resets forms and clear validation when bootstrap modal is closed
   * @description We want to reset forms to their original values when the modal is closed
   */
  const resetFormsOnModalClose = function () {

    // Get all bootstrap modals
    const modals = document.querySelectorAll('.modal');
    modals.forEach(modal => {

      // Listen to modal close event
      modal.addEventListener('hidden.bs.modal', function () {

        // Clear validation classes
        const classes = ['is-valid', 'is-invalid', 'is-dirty'];
        const elems = this.querySelectorAll(classes.map(v => '.' + v).join(', '));
        elems.forEach(elem => elem.classList.remove(...classes));

        // Remove validation messages
        const delElems = this.querySelectorAll('.invalid-feedback > *');
        delElems.forEach(elem => elem.remove());

        // Reset form
        const form = this.querySelector('form');
        if (form) form.reset();
      });
    });
  };

  /**
   * @summary Init copy to clipboard on click for elements with 'data-clipboard-text' attribute
   */
  const initClipboard = function () {

    // Any element with attribute data-clipboard-text will be copied to clipboard when clicked
    const clipboardButtonSelector = '[data-clipboard-text]';

    // Show tooltip when button is clicked
    const clipboardButtons = document.querySelectorAll(clipboardButtonSelector);
    clipboardButtons.forEach(button => {

      // Add a bootstrap tooltip attributes to element, and init tooltip
      button.setAttribute('data-bs-toggle', 'tooltip');
      button.setAttribute('data-bs-placement', 'top');
      button.setAttribute('data-bs-trigger', 'manual');
      button.setAttribute('data-bs-delay-hide', '3000');
      button.setAttribute('title', 'Copied to clipboard!');
      const tooltip = new window.bootstrap.Tooltip(button);

      // Init clipboard
      const clip = new ClipboardJS(button);
      let timeout;

      // Show tooltip when button is clicked, and hide after 3 seconds
      clip.on('success', () => {
        if (timeout) clearTimeout(timeout);
        tooltip.show();
        timeout = setTimeout(() => { tooltip.hide(); }, 3000);
      });
    });
  };


  /**
   * @summary Improve accessibility for certain custom form elements
   */
  const improveFormAccessibility = function () {

    // Improve keyboard accessibility for hidden radio buttons
    document.querySelectorAll('.radio-label-pair + label').forEach(el => {
      el.addEventListener('keydown', function (evt) {
        // Only listen to spacebar or enter event
        if (evt.key !== 32 && evt.key !== 'Enter') return;
        this.previousElementSibling.click();
      });
    });

    // Toggle password visibility
    document.querySelectorAll('.toggle-pw').forEach(el => {
      el.addEventListener('click', function () {
        const input = this.previousElementSibling;
        const newType = input.getAttribute('type') === 'password' ? 'text' : 'password';
        input.setAttribute('type', newType);
        this.querySelector('i').classList.toggle('bi-eye');
      });
    });
  };


  return {
    init: function () {

      // Ensure this is only run once
      const htmlEl = document.documentElement;
      if (htmlEl.hasAttribute('app-init')) return;
      else htmlEl.setAttribute('app-init', '');

      // Call sub-functions on page load
      initDarkModeToggle();
      activeMenuLinks();
      inputMasking();
      handlePageLevelAlerts();
      updateWindowScrollPosition();
      resetFormsOnModalClose();
      initClipboard();
      formatDates();
      improveFormAccessibility();

      // Add class loaded to html element when completed
      htmlEl.classList.add('loaded');
    },
  };
};
pageReady(app().init);